import React, { useState, useEffect, useCallback, useRef } from 'react';

const COLORS = {
  topFill: '#D0CBFC',      // New logins (light purple top)
  bottomFill: '#5D29EC',   // Deleted logins (dark purple bottom)
  legendDeleted: '#5325D4',
  legendNew: '#9A85D4',
  trendline: '#281B4B',
  labelColor: '#616E7C',
  titleColor: '#1F2933',
  subtle: '#9AA5B4',
  border: '#CBD2D9',
};

// Reference starts at MAY 2024 and wraps around to APR 2025
const MONTHS = [
  'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT',
  'NOV', 'DEC', 'JAN', 'FEB', 'MAR', 'APR',
];

const MONTH_FULL = [
  'May', 'June', 'July', 'August', 'September', 'October',
  'November', 'December', 'January', 'February', 'March', 'April',
];

const MONTH_YEARS = [
  2024, 2024, 2024, 2024, 2024, 2024,
  2024, 2024, 2025, 2025, 2025, 2025,
];

// Final data: [newLogins (top), deletedLogins (bottom)] per month (extracted from SVG reference)
const finalData: [number, number][] = [
  [23, 23], // MAY
  [16, 16], // JUN
  [18, 18], // JUL
  [24, 24], // AUG
  [14, 14], // SEP
  [15, 4],  // OCT
  [7, 10],  // NOV
  [0, 11],  // DEC
  [4, 20],  // JAN
  [9, 9],   // FEB
  [9, 9],   // MAR
  [9, 9],   // APR
];

// Trendline values (Total logins) extracted/approximated from SVG path y coordinates
// Higher = more total logins. Represented as units above baseline.
const trendData: number[] = [15, 17, 15, 15, 15, 10, 13, 15, 12, 13, 13, 14];

const BAR_COUNT = 12;
const CHART_HEIGHT = 120;
const BASELINE_Y = CHART_HEIGHT / 2;
const MAX_VALUE = 26;
const PIXEL_PER_UNIT = (CHART_HEIGHT / 2) / MAX_VALUE;

// Balance animation: each month has its own oscillation personality —
// different phases, frequencies, sizes and a deletion bias that emerges
// as the oscillation decays, leaning the chart toward the "deleted" end.
const BALANCE_DURATION = 2400;
const SETTLE_DURATION = 600;
const TOTAL_LOADING = BALANCE_DURATION + SETTLE_DURATION;

// Per-bar traits (seeded for stability): [phaseOffset, freqMult, size, deleteBias]
// deleteBias: negative = leans delete-heavy as oscillation decays
const BAR_TRAITS: [number, number, number, number][] = [
  [0.0,   1.00, 16, -0.25],  // MAY  — big, slight delete lean
  [1.8,   0.85, 13, -0.15],  // JUN
  [0.9,   1.10, 15, -0.35],  // JUL  — moderate delete lean
  [2.5,   0.95, 17, -0.20],  // AUG  — biggest
  [1.2,   1.15, 12, -0.40],  // SEP  — strong delete lean
  [3.1,   0.80, 10, -0.55],  // OCT  — heavy delete
  [0.5,   1.05, 9,  -0.60],  // NOV  — small, heavy delete
  [2.0,   0.90, 8,  -0.70],  // DEC  — smallest, most delete-heavy
  [3.8,   1.20, 13, -0.50],  // JAN  — recovery, still delete-leaning
  [1.5,   1.00, 10, -0.30],  // FEB
  [2.8,   0.88, 10, -0.30],  // MAR
  [0.3,   1.08, 10, -0.25],  // APR
];

function generateBalanceData(time: number, amplitude: number = 1): { bars: [number, number][]; trend: number[] } {
  const bars: [number, number][] = [];
  const trend: number[] = [];
  const baseFreq = 0.0038;
  const dampRate = 0.0009;

  for (let i = 0; i < BAR_COUNT; i++) {
    const [phaseOff, freqMult, size, deleteBias] = BAR_TRAITS[i];
    const localTime = Math.max(0, time - i * 35);

    // Decaying envelope
    const env = Math.exp(-localTime * dampRate);

    // Each bar oscillates at its own frequency and phase
    const osc = Math.cos(localTime * baseFreq * freqMult + phaseOff) * env * amplitude;

    // As oscillation decays, the per-bar deletion bias emerges
    const biasStrength = (1 - env) * deleteBias;

    // Combined balance: oscillation + growing deletion bias
    const balance = osc + biasStrength;

    const half = size / 2;
    const shift = balance * half * 1.3;
    const top = Math.max(0, Math.round(half + shift));
    const bottom = Math.max(0, Math.round(half - shift));
    bars.push([top, bottom]);
    trend.push(13 + balance * 6);
  }
  return { bars, trend };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function getTotals(top: number, bottom: number, trend: number) {
  // Scale mock values to realistic totals
  return {
    newCount: top * 3 + 4,
    deletedCount: bottom * 2 + 2,
    totalCount: Math.round(trend * 6 + 20),
  };
}

// Build a smooth Catmull-Rom style path for the trendline through points
function buildTrendPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

const GlobeIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 12C21 16.9706 16.9706 21 12 21M21 12C21 7.02944 16.9706 3 12 3M21 12H3M12 21C7.02944 21 3 16.9706 3 12M12 21C13.6569 21 15 16.9706 15 12C15 7.02944 13.6569 3 12 3M12 21C10.3431 21 9 16.9706 9 12C9 7.02944 10.3431 3 12 3M3 12C3 7.02944 7.02944 3 12 3"
      stroke="#111827"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LoginsChart: React.FC = () => {
  const [bars, setBars] = useState<[number, number][]>(() => generateBalanceData(0).bars);
  const [trend, setTrend] = useState<number[]>(() => generateBalanceData(0).trend);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastWaveRef = useRef<{ bars: [number, number][]; trend: number[] }>(generateBalanceData(0));
  const rafRef = useRef<number>(0);
  const chartRef = useRef<HTMLDivElement>(null);

  const animate = useCallback((timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = timestamp - startTimeRef.current;

    if (elapsed < BALANCE_DURATION) {
      // Gentle additional dampening over the final 30% for a silky handoff
      const progress = elapsed / BALANCE_DURATION;
      const dampStart = 0.7;
      const amplitude = progress < dampStart
        ? 1
        : 1 - Math.pow((progress - dampStart) / (1 - dampStart), 2) * 0.6;
      const wave = generateBalanceData(elapsed, amplitude);
      lastWaveRef.current = wave;
      setBars(wave.bars);
      setTrend(wave.trend);
      rafRef.current = requestAnimationFrame(animate);
    } else if (elapsed < TOTAL_LOADING) {
      const settleElapsed = elapsed - BALANCE_DURATION;
      const t = easeOutCubic(Math.min(1, settleElapsed / SETTLE_DURATION));
      const lastWave = lastWaveRef.current;
      const blendedBars: [number, number][] = finalData.map(([ft, fb], i) => {
        const [wt, wb] = lastWave.bars[i] || [ft, fb];
        return [Math.round(lerp(wt, ft, t)), Math.round(lerp(wb, fb, t))];
      });
      const blendedTrend: number[] = trendData.map((ft, i) => {
        const wt = lastWave.trend[i] ?? ft;
        return lerp(wt, ft, t);
      });
      setBars(blendedBars);
      setTrend(blendedTrend);
      rafRef.current = requestAnimationFrame(animate);
    } else {
      setBars(finalData);
      setTrend(trendData);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [animate]);

  const handleMouseEnter = (index: number, e: React.MouseEvent) => {
    if (isLoading) return;
    setHoveredIndex(index);
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isLoading) return;
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setTooltipPos(null);
  };

  // Layout math
  const chartWidth = 1132;
  const gap = 4;
  const barWidth = (chartWidth - gap * (BAR_COUNT - 1)) / BAR_COUNT;

  // Trendline points (bar centers + margins)
  const trendPoints = trend.map((v, i) => ({
    x: i * (barWidth + gap) + barWidth / 2,
    y: BASELINE_Y - v * PIXEL_PER_UNIT,
  }));
  // Extend trendline to chart edges for continuity
  const extendedTrendPoints = [
    { x: 0, y: trendPoints[0].y },
    ...trendPoints,
    { x: chartWidth, y: trendPoints[trendPoints.length - 1].y },
  ];
  const trendPath = buildTrendPath(extendedTrendPoints);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '8px',
        border: `1px solid ${COLORS.border}`,
        padding: '20px 24px 16px',
        width: '100%',
        maxWidth: 'var(--chart-max-width, 1180px)',
        boxSizing: 'border-box' as const,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <GlobeIcon />
          <span
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: COLORS.titleColor,
              letterSpacing: '-0.01em',
            }}
          >
            Online footprint trend
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <LegendDot color={COLORS.legendDeleted} label="Deleted logins" />
          <LegendDot color={COLORS.legendNew} label="New logins" />
          <LegendLine label="Total logins" />
        </div>
      </div>

      {/* Chart */}
      <div
        ref={chartRef}
        style={{ position: 'relative', width: '100%' }}
        onMouseMove={handleMouseMove}
      >
        <style>{`
          .login-bar-group { transition: opacity 0.25s ease; }
        `}</style>
        <svg
          width="100%"
          height={CHART_HEIGHT + 28}
          viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT + 28}`}
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          {/* Bars */}
          {bars.map(([top, bottom], i) => {
            const x = i * (barWidth + gap);
            const topHeight = top * PIXEL_PER_UNIT;
            const bottomHeight = bottom * PIXEL_PER_UNIT;
            const isDimmed = hoveredIndex !== null && hoveredIndex !== i;
            const r = Math.min(barWidth / 2, 4);
            const barTop = BASELINE_Y - topHeight;

            return (
              <g
                key={i}
                className="login-bar-group"
                opacity={isDimmed ? 0.3 : 1}
                onMouseEnter={(e) => handleMouseEnter(i, e)}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: isLoading ? 'default' : 'pointer' }}
              >
                {/* Invisible hover hit area */}
                <rect
                  x={x}
                  y={0}
                  width={barWidth}
                  height={CHART_HEIGHT}
                  fill="transparent"
                />
                <PillHalf
                  x={x}
                  y={barTop}
                  width={barWidth}
                  top={topHeight}
                  bottom={bottomHeight}
                  r={r}
                  colorTop={COLORS.topFill}
                  colorBottom={COLORS.bottomFill}
                />
              </g>
            );
          })}

          {/* Trendline */}
          <path
            d={trendPath}
            stroke={COLORS.trendline}
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: 'none' }}
          />

          {/* Month labels */}
          {MONTHS.map((m, i) => {
            const x = i * (barWidth + gap) + barWidth / 2;
            return (
              <text
                key={i}
                x={x}
                y={CHART_HEIGHT + 20}
                textAnchor="middle"
                fontSize={12}
                fontWeight={500}
                fill={COLORS.labelColor}
                fontFamily="'Inter', -apple-system, sans-serif"
              >
                {m}
              </text>
            );
          })}
        </svg>

        {/* Tooltip */}
        {!isLoading && hoveredIndex !== null && tooltipPos && (() => {
          const [top, bottom] = bars[hoveredIndex];
          const trendVal = trend[hoveredIndex];
          const { newCount, deletedCount, totalCount } = getTotals(top, bottom, trendVal);
          const month = MONTH_FULL[hoveredIndex];
          const year = MONTH_YEARS[hoveredIndex];
          const containerW = chartRef.current?.clientWidth ?? 1000;
          const tipLeft = Math.min(Math.max(tooltipPos.x + 16, 0), containerW - 240);
          return (
            <div
              style={{
                position: 'absolute',
                left: tipLeft,
                top: Math.max(0, tooltipPos.y - 100),
                background: 'white',
                borderRadius: '10px',
                padding: '14px 16px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
                border: '1px solid #E4E7EB',
                minWidth: '220px',
                fontFamily: "'Inter', -apple-system, sans-serif",
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: COLORS.legendDeleted, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: COLORS.titleColor }}>
                  <strong>{deletedCount}</strong> Deleted logins
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: COLORS.legendNew, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: COLORS.titleColor }}>
                  <strong>{newCount}</strong> New logins
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 12, height: 2, backgroundColor: COLORS.trendline, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: COLORS.titleColor }}>
                  <strong>{totalCount}</strong> Total logins
                </span>
              </div>
              <div style={{ fontSize: 13, color: COLORS.subtle }}>
                During {month} {year}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const PillHalf: React.FC<{
  x: number;
  y: number;
  width: number;
  top: number;
  bottom: number;
  r: number;
  colorTop: string;
  colorBottom: string;
}> = ({ x, y, width, top, bottom, r, colorTop, colorBottom }) => {
  const baseline = y + top;
  const bottomY = baseline + bottom;
  const rightX = x + width;

  const topPath = top > 0
    ? `M ${x} ${y + r}
       Q ${x} ${y} ${x + r} ${y}
       L ${rightX - r} ${y}
       Q ${rightX} ${y} ${rightX} ${y + r}
       L ${rightX} ${baseline}
       L ${x} ${baseline} Z`
    : '';

  const bottomPath = bottom > 0
    ? `M ${x} ${baseline}
       L ${rightX} ${baseline}
       L ${rightX} ${bottomY - r}
       Q ${rightX} ${bottomY} ${rightX - r} ${bottomY}
       L ${x + r} ${bottomY}
       Q ${x} ${bottomY} ${x} ${bottomY - r} Z`
    : '';

  return (
    <>
      {topPath && <path d={topPath} fill={colorTop} />}
      {bottomPath && <path d={bottomPath} fill={colorBottom} />}
    </>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: 2,
        backgroundColor: color,
      }}
    />
    <span style={{ fontSize: 12, color: COLORS.labelColor, fontWeight: 500 }}>
      {label}
    </span>
  </div>
);

const LegendLine: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <div
      style={{
        width: 14,
        height: 2,
        backgroundColor: COLORS.trendline,
        borderRadius: 1,
      }}
    />
    <span style={{ fontSize: 12, color: COLORS.labelColor, fontWeight: 500 }}>
      {label}
    </span>
  </div>
);
